import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import ProjectBox from "../Elements/ProjectBox";
import AddImage2 from "../../assets/img/about.png";
import ServiceAPI from "../../services/NavaService";
import { useNavigate } from "react-router-dom";

export default function Projects() {
  const [services, setServices] = useState([]);
  const createdUrlsRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchServicesWithImages();
    return () => {
      createdUrlsRef.current.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (e) {}
      });
      createdUrlsRef.current = [];
    };
  }, []);

  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const fetchServicesWithImages = async () => {
    try {
      const res = await ServiceAPI.getServices();
      const data = res.data || [];

      const normalized = data.map((s) => ({
        ...s,
        srvId: s.srvId ?? s.id ?? s.ngs_id ?? s.ngsId ?? s.srv_id ?? null
      }));

      const enriched = await Promise.all(
        normalized.map(async (svc) => {
          const out = { ...svc, previewUrl: null };
          const srvId = svc.srvId;
          if (!srvId) return out;

          try {
            const listRes = await ServiceAPI.listImages(srvId);
            const meta = listRes.data || [];
            if (meta.length === 0) return out;

            const first = meta[0];
            const imgId = first?.imgId ?? first?.id ?? first?.img_id ?? first?.nsi_id ?? null;
            if (!imgId) return out;

            const imgResp = await ServiceAPI.getImage(srvId, imgId);
            const url = URL.createObjectURL(imgResp.data);
            createdUrlsRef.current.push(url);
            out.previewUrl = url;
            return out;
          } catch (err) {
            console.warn("Failed to load preview for service", svc, err);
            return out;
          }
        })
      );

      setServices(enriched);
    } catch (err) {
      console.error("Failed to fetch services", err);
      setServices([]);
    }
  };

  const getRequestedSlidesPerView = useCallback(() => {
    const w = window.innerWidth;
    if (w <= 640) return 1;
    if (w <= 1024) return 2;
    return 3;
  }, []);

  const [slidesPerView, setSlidesPerView] = useState(getRequestedSlidesPerView());
  const [index, setIndex] = useState(0);

  const timerRef = useRef(null);
  const servicesLenRef = useRef(0);
  const effectiveSlidesRef = useRef(1);
  const maxIndexRef = useRef(0);

  useEffect(() => { servicesLenRef.current = services.length; }, [services.length]);
  useEffect(() => { effectiveSlidesRef.current = Math.min(Math.max(1, slidesPerView), Math.max(1, servicesLenRef.current)); }, [slidesPerView, services.length]);
  useEffect(() => { maxIndexRef.current = Math.max(0, servicesLenRef.current - effectiveSlidesRef.current); }, [services.length, slidesPerView]);

  useEffect(() => {
    const onResize = () => setSlidesPerView(getRequestedSlidesPerView());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [getRequestedSlidesPerView]);

  const delayMs = 3000; 
  
  const stopAutoplay = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay();

    if (servicesLenRef.current <= effectiveSlidesRef.current) return;

    const tick = () => {
      setIndex((cur) => {
        const mi = maxIndexRef.current;
        return cur >= mi ? 0 : cur + 1;
      });
      timerRef.current = setTimeout(tick, delayMs);
    };

    timerRef.current = setTimeout(tick, delayMs);
  }, [stopAutoplay]);

  useEffect(() => {
    setIndex((cur) => (cur > maxIndexRef.current ? 0 : cur));

    startAutoplay();

    return () => {
      stopAutoplay();
    };
  }, [services.length, slidesPerView, startAutoplay, stopAutoplay]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [startAutoplay, stopAutoplay]);

  // handlers
  const restartAutoplay = useCallback(() => {
    stopAutoplay();
    startAutoplay();
  }, [startAutoplay, stopAutoplay]);

  const nextSlide = useCallback(() => {
    if (servicesLenRef.current <= effectiveSlidesRef.current) return;
    setIndex((cur) => (cur >= maxIndexRef.current ? 0 : cur + 1));
    restartAutoplay();
  }, [restartAutoplay]);

  const prevSlide = useCallback(() => {
    if (servicesLenRef.current <= effectiveSlidesRef.current) return;
    setIndex((cur) => (cur <= 0 ? maxIndexRef.current : cur - 1));
    restartAutoplay();
  }, [restartAutoplay]);

  const effectiveSlides = Math.min(Math.max(1, slidesPerView), Math.max(1, services.length));
  const translatePercent = -(index * (100 / effectiveSlides));

  return (
    <Wrapper id="projects">
      <div className="whiteBg">
        <div className="container">
          <HeaderInfo>
            <h1 className="font40 extraBold">Our Services</h1>
            <p className="font13">
              From solar power systems and energy storage to efficiency consulting,
              Nava Energy provides reliable services designed to help you save costs, <br />embrace sustainability,
              and power your future with confidence.
            </p>
          </HeaderInfo>
          <div className="sliderWrap">
            <button className="ctrlBtn prevBtn" onClick={prevSlide} aria-label="Previous slide">
              ‹
            </button>

            <div className="sliderViewport">
              <div
                className="sliderTrack"
                style={{
                  transform: `translateX(${translatePercent}%)`,
                  transition: "transform 600ms ease"
                }}
              >
                {services.map((s) => {
                  const srvId = s.srvId ?? s.id ?? "";
                  return (
                    <div
                      className="slide"
                      key={srvId || Math.random()}
                      style={{ flex: `0 0 ${100 / effectiveSlides}%` }}
                    >
                      <div className="project-card" style={{ padding: "0 12px" }}>
                        <ProjectBox
                          img={s.previewUrl ?? AddImage2}
                          title={s.name ?? s.service_name ?? "Service"}
                          text={""} 
                          action={() => {
                            if (!srvId) {
                              alert("Service id not available");
                              return;
                            }
                            stopAutoplay();
                            navigate(`/service-desc/${srvId}`);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button className="ctrlBtn nextBtn" onClick={nextSlide} aria-label="Next slide">
              ›
            </button>
          </div>

          {services.length === 0 && (
            <div className="row textCenter">
              <div className="col-xs-12">
                <p>No services available yet.</p>
              </div>
            </div>
          )}

          <div className="row flexCenter">
            <div style={{ margin: "50px 0", width: "200px" }}>
            </div>
          </div>
        </div>
      </div>

      <div className="lightBg">
        <div className="container">
          <Advertising className="flexSpaceCenter">
            <AddLeft>
              <AddLeftInner>
                <ImgWrapper className="flexCenter">
                  <img className="radius8" src={AddImage2} alt="add" />
                </ImgWrapper>
              </AddLeftInner>
            </AddLeft>
            <AddRight>
              <h4 className="font18 semiBold">A few words about us</h4>
              <h2 className="font40 extraBold">At Nava Energy,</h2>
              <p className="font16">
               we are committed to delivering sustainable and reliable energy solutions that power communities, businesses,
               and industries. Our services span across solar energy, power management, and innovative clean technologies
               designed to reduce costs and carbon footprints. We combine advanced engineering with a customer-first approach
               to ensure every project not only meets today’s needs but also builds a brighter, greener future.
              </p>
            </AddRight>
          </Advertising>
        </div>
      </div>
    </Wrapper>
  );
}

const HeaderInfo = styled.div`
  @media (max-width: 860px) {
    text-align: center;
  }
`;

const Wrapper = styled.section`
  width: 100%;

  .sliderWrap {
    position: relative;
  }

  .sliderViewport {
    width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    padding: 20px 0;
  }

  .sliderTrack {
    display: flex;
    align-items: stretch;
    will-change: transform;
  }

  .slide {
    box-sizing: border-box;
    display: flex;
    align-items: stretch;
  }

  .prevBtn {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 12;
  }

  .nextBtn {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 12;
  }

  .ctrlBtn {
    background: rgba(0,0,0,0.65);
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
  .ctrlBtn:active { transform: translateY(1px); }
  .ctrlBtn:focus { outline: 2px solid rgba(255,255,255,0.25); }

  @media (max-width: 640px) {
    .project-card {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .project-card img {
      width: auto;
      max-width: 80%;
      height: auto;
      margin: 0 auto 12px;
    }
    .slide { justify-content: center; }
    .prevBtn { left: 6px; }
    .nextBtn { right: 6px; }
    .ctrlBtn { padding: 6px 8px; font-size: 13px; }
  }
    .prevBtn { left: 6px; }
    .nextBtn { right: 6px; }
    .ctrlBtn { padding: 6px 8px; font-size: 13px; }
  }

  .project-card img {
    width: 100%;
    height: 220px;
    display: block;
    object-fit: cover;
    object-position: center;
    border-radius: 8px;
  }

  @media (max-width: 920px) {
    .project-card img { height: 180px; }
  }
  @media (max-width: 480px) {
    .project-card img { height: 140px; }
  }
`;

const Advertising = styled.div`
  padding: 100px 0;
  margin: 100px 0;
  position: relative;
  @media (max-width: 1160px) {
    padding: 60px 0 40px 0;
  }
  @media (max-width: 860px) {
    flex-direction: column;
    padding: 0 0 30px 0;
    margin: 80px 0 0px 0;
  }
`;
const AddLeft = styled.div`
  position: relative;
  width: 50%;
  p {
    max-width: 475px;
  }
  @media (max-width: 860px) {
    width: 80%;
    order: 2;
    text-align: center;
    h2 {
      line-height: 3rem;
      margin: 15px 0;
    }
    p {
      margin: 0 auto;
    }
  }
`;
const AddRight = styled.div`
  width: 50%;
  @media (max-width: 860px) {
    width: 80%;
    order: 2;
  }
`;
const AddLeftInner = styled.div`
  width: 100%;
  position: absolute;
  top: -300px;
  left: 0;
  @media (max-width: 1190px) {
    top: -250px;
  }
  @media (max-width: 920px) {
    top: -200px;
  }
  @media (max-width: 860px) {
    order: 1;
    position: relative;
    top: -60px;
    left: 0;
  }
`;
const ImgWrapper = styled.div`
  width: 100%;
  padding: 0 15%;

  .fixedImg {
    width: 100%;
    height: 200px;          
    display: block;
    object-fit: cover;      
    object-position: center; 
    border-radius: 8px;     
  }

  img {
    width: 100%;
    height: auto;
  }

  @media (max-width: 400px) {
    padding: 0;
  }
`;
